import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button, TextField } from "@bloo-team/ui";
import { Backdrop } from '@bloo-team/ui';
import { BlooLogo } from '@bloo-team/core-shell';
import {
  DEMO_MODE_AVAILABLE,
  LIVE_ORIGIN_LABEL,
  setAuthMode,
  useAuthMode,
} from "../../lib/api";
import { signIn, signInWithSsid, verifyOtp } from "../../lib/auth";
import { getSession, defaultLocation } from "../../lib/session";

/**
 * Login. Mirrors the legacy flow (init -> login -> optional verify-otp, plus
 * SAML SSO) on the new design language: glass panel over the comet backdrop.
 *
 * Demo mode: any credentials sign in · demo-otp@bloo.io exercises the OTP
 * step · fail@bloo.io shows the error state. Real calls land with the
 * api-client (PLAN.md Phase 3 auth consolidation).
 */

type Step =
  | { kind: "credentials" }
  | { kind: "otp"; challengeId: string }
  | { kind: "ssid" };

function LoginPage() {
  const navigate = useNavigate();
  const mode = useAuthMode();
  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [ssid, setSsid] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enter = () => {
    const session = getSession();
    if (!session) return;
    const { clusterId, scopeId } = defaultLocation(session);
    void navigate({
      to: "/$clusterId/$scopeId/home",
      params: { clusterId, scopeId },
      replace: true,
    });
  };

  /**
   * Values come from the submitted form, not from React state.
   *
   * Password managers (and some browsers' autofill) write straight to
   * `input.value` without firing an event React can see. The controlled state
   * then stays empty while the field visibly contains a password — which used
   * to leave the Sign in button permanently disabled, so the page looked
   * broken and did nothing. The DOM is the truth at submit time; state is only
   * the fallback for the browsers that do fire events.
   */
  const submitCredentials = async (formEmail: string, formPassword: string) => {
    if (busy) return;
    const e = formEmail || email;
    const p = formPassword || password;
    if (!e || !p) {
      setError("Enter your email and password.");
      return;
    }
    // Autofilled values arrive via FormData, not change events — sync the
    // merged email back so the OTP step's "code was sent to X" names it.
    setEmail(e);
    setBusy(true);
    setError(null);
    const res = await signIn(e, p);
    setBusy(false);
    if (res.kind === "session") enter();
    else if (res.kind === "otp-required")
      setStep({ kind: "otp", challengeId: res.challengeId });
    else setError(res.message);
  };

  // The only live path that works while /lc/users/login is stubbed: an SSID
  // lifted from a working browser session, exchanged for access-details.
  // See docs/inventory/AUTH-DIAGNOSIS.md.
  // Both take the form's own value at submit and merge with state — the same
  // autofill rule as the credentials form: an OS-filled OTP or a pasted SSID
  // may never have raised a React change event, so state alone can be empty
  // while the field visibly holds the value.
  const submitSsid = async (formSsid: string) => {
    if (busy) return;
    const s = (formSsid || ssid).trim();
    if (!s) {
      setError("Paste a session ID first.");
      return;
    }
    setBusy(true);
    setError(null);
    const res = await signInWithSsid(s);
    setBusy(false);
    if (res.kind === "session") enter();
    else if (res.kind === "error") setError(res.message);
  };

  const submitOtp = async (formCode: string) => {
    if (step.kind !== "otp" || busy) return;
    const c = (formCode || code).replace(/\D/g, "").slice(0, 6);
    if (c.length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    setBusy(true);
    setError(null);
    const res = await verifyOtp(step.challengeId, c);
    setBusy(false);
    if (res.kind === "session") enter();
    else if (res.kind === "error") setError(res.message);
  };

  return (
    <div className="login">
      <Backdrop />
      <main className="login-panel" aria-label="Sign in">
        <div className="login-brand">
          <BlooLogo height={26} />
          <span className="hud-eyebrow">console</span>
        </div>

        {DEMO_MODE_AVAILABLE ? (
          <div className="login-mode" role="group" aria-label="Environment">
            <button
              type="button"
              className="login-mode-btn"
              aria-pressed={mode === "demo"}
              onClick={() => {
                setAuthMode("demo");
                setError(null);
              }}
            >
              Demo
            </button>
            <button
              type="button"
              className="login-mode-btn"
              aria-pressed={mode === "live"}
              onClick={() => {
                setAuthMode("live");
                setError(null);
              }}
            >
              Live
            </button>
            <span className="login-mode-target hud-eyebrow">
              {mode === "live" ? <>· {LIVE_ORIGIN_LABEL}</> : <>· sample data</>}
            </span>
          </div>
        ) : null}

        {step.kind === "ssid" ? (
          <form
            className="login-form"
            onSubmit={(e) => {
              const data = new FormData(e.currentTarget);
              e.preventDefault();
              void submitSsid(String(data.get("ssid") ?? ""));
            }}
          >
            <p className="login-hint">
              Paste an <b>SSID</b> from a signed-in session on{" "}
              {LIVE_ORIGIN_LABEL} (devtools → Network → any request → the{" "}
              <code>SSID</code> request header). Your access is loaded from it
              directly.
            </p>
            <TextField
              label="Session ID (SSID)"
              name="ssid"
              value={ssid}
              onChange={setSsid}
              placeholder="SSID"
            />
            {error ? (
              <div className="bui-alert bui-alert-error" role="alert">
                <span className="bui-alert-mark">✕</span>
                <span>{error}</span>
              </div>
            ) : null}
            <Button type="submit" variant="solid" fullWidth isDisabled={busy}>
              {busy ? "Loading access…" : "Continue"}
            </Button>
            <div className="login-links">
              <button
                type="button"
                className="bui-link bui-link-muted login-linkbtn"
                onClick={() => {
                  setStep({ kind: "credentials" });
                  setError(null);
                  setSsid("");
                }}
              >
                ← Back to sign in
              </button>
            </div>
          </form>
        ) : step.kind === "credentials" ? (
          <form
            className="login-form"
            onSubmit={(e) => {
              const data = new FormData(e.currentTarget);
              e.preventDefault();
              void submitCredentials(
                String(data.get("email") ?? ""),
                String(data.get("password") ?? ""),
              );
            }}
          >
            <TextField
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              placeholder="you@company.com"
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
            />
            {error ? (
              <div className="bui-alert bui-alert-error" role="alert">
                <span className="bui-alert-mark">✕</span>
                <span>{error}</span>
              </div>
            ) : null}
            <Button type="submit" variant="solid" fullWidth isDisabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <div className="bui-divider-label" role="separator">
              or
            </div>
            <Button variant="outline" fullWidth isDisabled={busy}>
              Continue with SSO
            </Button>
            <div className="login-links">
              <a
                className="bui-link bui-link-muted"
                href="#/auth/reset-password"
              >
                Forgot password?
              </a>
              {mode === "live" ? (
                <>
                  <span aria-hidden="true"> · </span>
                  <button
                    type="button"
                    className="bui-link bui-link-muted login-linkbtn"
                    onClick={() => {
                      setStep({ kind: "ssid" });
                      setError(null);
                    }}
                  >
                    Use an existing session ID
                  </button>
                </>
              ) : null}
            </div>
          </form>
        ) : (
          <form
            className="login-form"
            onSubmit={(e) => {
              const data = new FormData(e.currentTarget);
              e.preventDefault();
              void submitOtp(String(data.get("code") ?? ""));
            }}
          >
            <p className="login-hint">
              A 6-digit code was sent to <b>{email}</b>. Enter it to continue.
            </p>
            <TextField
              label="Verification code"
              name="code"
              autoComplete="one-time-code"
              value={code}
              onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
            />
            {error ? (
              <div className="bui-alert bui-alert-error" role="alert">
                <span className="bui-alert-mark">✕</span>
                <span>{error}</span>
              </div>
            ) : null}
            <Button type="submit" variant="solid" fullWidth isDisabled={busy}>
              {busy ? "Verifying…" : "Verify"}
            </Button>
            <div className="login-links">
              <button
                type="button"
                className="bui-link bui-link-muted login-linkbtn"
                onClick={() => {
                  setStep({ kind: "credentials" });
                  setError(null);
                  setCode("");
                }}
              >
                ← Back to sign in
              </button>
            </div>
          </form>
        )}

        {DEMO_MODE_AVAILABLE && mode === "demo" ? (
          <p className="login-demo hud-eyebrow">
            any credentials work · demo-otp@bloo.io → otp · fail@bloo.io → error
          </p>
        ) : (
          <p className="login-demo hud-eyebrow">
            live · credentials are sent to {LIVE_ORIGIN_LABEL}
          </p>
        )}
      </main>
    </div>
  );
}

export const Route = createFileRoute("/auth/login")({ component: LoginPage });
